import { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumb from '../Breadcrumb';
import type { BreadcrumbItem } from '../Breadcrumb';
import ReturnTrail, { useReturnTrail, type InnerLevel } from './ReturnTrail';

/**
 * The band between the app chrome and the page body.
 *
 * WHAT THIS REPLACES. `Layout` draws the nav, the stage row and the card, then
 * renders a bare `<Outlet/>` — so every screen invented its own answer to
 * "where am I, and how do I get back". The result was nine different `<h1>`
 * treatments across the 22 pages that had one, twenty-six pages with no heading
 * at all (including `/settings`, whose only `<h1>` was dummy copy inside a
 * preview mock), and the subtitle sometimes above the action row and sometimes
 * dragged under it with a negative margin.
 *
 * The order here is the contract, and it is the same on every screen:
 *
 *   1. where you are (Breadcrumb, left) and the way back out of a deep-link
 *      (ReturnTrail, right) — one row, and it collapses when neither exists
 *   2. what this page is, and what you can do about it
 *
 * WIDTH AND PADDING ARE NOT ITS JOB. `Layout` owns those (`max-w-7xl`, and the
 * card's `p-4 sm:p-6`). A page that adds its own `p-6 max-w-*` on top double-pads
 * itself and lands at a different width from its siblings.
 */

/** The page title, one scale. Exported so the full-height editors — which own
 *  their own compact headers and cannot use this component — can still agree
 *  with it rather than each picking a size. */
export const PAGE_TITLE_CLASS =
  'text-xl sm:text-2xl font-bold text-gray-900 dark:text-gray-100';

/** The title of a workspace whose header is sized against the viewport. Smaller
 *  on purpose: those screens spend their vertical budget on the work. */
export const WORKSPACE_TITLE_CLASS =
  'text-lg font-semibold text-gray-900 dark:text-gray-100';

export const PAGE_SUBTITLE_CLASS = 'text-sm text-gray-500 dark:text-gray-400';

interface PageHeaderProps {
  title: ReactNode;
  /** One line on what the screen is for. Sits under the title, never under the
   *  action row — reading order should not depend on which page you are on. */
  subtitle?: ReactNode;
  /** The path to here. Omit on a list root, which is already the top. */
  breadcrumb?: BreadcrumbItem[];
  /** Primary actions, right-aligned against the title. */
  actions?: ReactNode;
  /** Small always-there chips (the models widget). Kept beside the actions so a
   *  page with both does not have to choose which one moves. */
  right?: ReactNode;
  /** Spacing override for hosts that own their own rhythm. */
  className?: string;
  /** Forwarded to ReturnTrail, for the one screen that still receives an older
   *  bespoke return param (`/trending?from_brainstorm=`). */
  returnFallback?: { path: string; label: string };
  /** A level open inside the page (a provider's editor). The back button
   *  closes it first — see ReturnTrail. */
  returnLevel?: InnerLevel | null;
  /** A "←" before the breadcrumb, to the crumb above this page. For a page
   *  under a list (a watched company under Watch), where small gray crumbs did
   *  not read as a way back. The same button the room's location bar has. */
  back?: boolean;
}

export default function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  right,
  className = 'mb-6',
  returnFallback,
  returnLevel,
  back = false,
}: PageHeaderProps) {
  // Asked BEFORE laying out the row: without this the header would render an
  // empty 12px band on every screen reached normally, which is most of them.
  const trail = useReturnTrail(returnFallback, returnLevel);
  const hasBreadcrumb = !!breadcrumb && breadcrumb.length > 0;
  // The crumb above this page: the last one with somewhere to go.
  const parent = [...(breadcrumb ?? [])].reverse().find(c => c.to);

  return (
    <div className={className}>
      {/* WHERE YOU ARE, LEFT. THE WAY OUT, RIGHT. One row.
          The trail used to be a chip on a row of its OWN above the breadcrumb,
          which cost a line on every deep-linked screen and put the same control
          in a different corner from the one `SettingsLayout` had always used.
          A back button people have to look for is the one thing navigation
          chrome cannot afford.

          It shares this row rather than the title row so it never competes with
          a page's own actions ("+ New Subject" and similar), which live opposite
          the title below. */}
      {(hasBreadcrumb || trail) && (
        <div className="flex items-start justify-between gap-4 mb-3">
          {hasBreadcrumb ? (
            <div className="flex min-w-0 items-center gap-2">
              {back && parent?.to && (
                <Link
                  to={parent.to}
                  title={`Back to ${parent.label}`}
                  aria-label={`Back to ${parent.label}`}
                  className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md border border-gray-200 text-gray-600 hover:bg-gray-100 hover:text-gray-900 dark:border-gray-700 dark:text-gray-300 dark:hover:bg-gray-700"
                  data-testid="page-back"
                >
                  ←
                </Link>
              )}
              <Breadcrumb items={breadcrumb!} className="mb-0 min-w-0" />
            </div>
          ) : (
            // Holds the left edge so `justify-between` still pushes the trail
            // right on a screen with no breadcrumb.
            <span />
          )}
          <ReturnTrail fallback={returnFallback} level={returnLevel} className="mb-0 shrink-0" />
        </div>
      )}
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h1 className={PAGE_TITLE_CLASS}>{title}</h1>
          {subtitle && <p className={`${PAGE_SUBTITLE_CLASS} mt-1`}>{subtitle}</p>}
        </div>
        {(right || actions) && (
          <div className="flex items-center gap-2 shrink-0">
            {right}
            {actions}
          </div>
        )}
      </div>
    </div>
  );
}
